import { useCreateModStatusMutation } from "@common/api/aircraft"
import { ataChaptersQuery } from "@common/api/ata-chapter"
import { currentUserQuery } from "@common/api/users"
import { Button } from "@common/components/ui/button"
import { Form } from "@common/components/ui/form"
import { useSharedForm } from "@common/hooks/use-shared-form"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import {
  DetailPane,
  DetailPanelActions,
  DetailPanelFields,
} from "@/components/layout/detail-panel"
import { PaneSections } from "@/components/page/pane"
import useCustomToast from "@/hooks/use-custom-toast"
import { ModStatusFields } from "../-components/mod-status/mod-status-fields"
import { modStatusLabel } from "../-lib/mod-status"
import {
  emptyModStatusForm,
  type ModStatusFormInput,
  modStatusFormSchema,
  newModStatusPayload,
} from "../-lib/mod-status-form"

export const Route = createFileRoute(
  "/_layout/aircraft_/$aircraftId/mod-status/new",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "mod_status:create")) {
      throw redirect({ to: "/aircraft/$aircraftId", params, search })
    }
  },
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(ataChaptersQuery()),
  component: NewModStatusPane,
})

function NewModStatusPane(): React.ReactElement {
  const { aircraftId } = Route.useParams()
  const navigate = useNavigate({ from: Route.fullPath })
  const showToast = useCustomToast()
  const mutation = useCreateModStatusMutation()
  const form = useSharedForm<ModStatusFormInput>({
    resolver: zodResolver(modStatusFormSchema),
    defaultValues: emptyModStatusForm,
    criteriaMode: "all",
    mode: "onBlur",
  })
  const close = (opened?: string) =>
    navigate({
      to: "/aircraft/$aircraftId",
      params: { aircraftId },
      search: (previous) => ({
        ...previous,
        tab: "modification-status" as const,
        mod_item: opened,
      }),
    })

  const submit = (values: ModStatusFormInput) =>
    mutation.mutate(
      { modStatusCreate: newModStatusPayload(values, aircraftId) },
      {
        onSuccess: (created) => {
          showToast("Created", `${modStatusLabel(created)} added.`, "success")
          close(created.id)
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <DetailPane title="New modification" onClose={() => close()}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(submit)}
          className="flex h-full flex-col"
        >
          <DetailPanelFields>
            <PaneSections>
              <ModStatusFields editable draft />
            </PaneSections>
          </DetailPanelFields>
          <DetailPanelActions
            dirty={form.formState.isDirty}
            pending={mutation.isPending}
            onReset={() => form.reset()}
            alwaysShowSave
          >
            <Button type="button" variant="outline" onClick={() => close()}>
              Cancel
            </Button>
          </DetailPanelActions>
        </form>
      </Form>
    </DetailPane>
  )
}
